import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Upload, Image, Loader2, GripVertical } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminBackgroundManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchBackgroundImages();
  }, []);

  const fetchBackgroundImages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_admin_token");
      const response = await axios.get(`${API}/admin/background-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data || []);
    } catch (error) {
      console.error("Erreur:", error);
      // If no custom images, that's fine
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.url) {
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        await addBackgroundImage(imageUrl);
        toast.success("Image ajoutée !");
      }
    } catch (error) {
      console.error("Erreur upload:", error);
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAddUrl = async () => {
    if (!newUrl.trim()) {
      toast.error("Veuillez entrer une URL");
      return;
    }
    await addBackgroundImage(newUrl.trim());
    setNewUrl("");
  };

  const addBackgroundImage = async (url) => {
    try {
      const token = localStorage.getItem("tika_admin_token");
      await axios.post(
        `${API}/admin/background-images`,
        { url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchBackgroundImages();
      toast.success("Image de fond ajoutée !");
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de l'ajout");
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm("Supprimer cette image de fond ?")) return;

    try {
      const token = localStorage.getItem("tika_admin_token");
      await axios.delete(`${API}/admin/background-images/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchBackgroundImages();
      toast.success("Image supprimée");
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold mb-2">Images de Fond</h2>
        <p className="text-sm text-muted-foreground">
          Gérez les images du mur de fond culturel. Ajoutez des pochettes d'albums, photos d'artistes, etc.
        </p>
      </div>

      {/* Add new image */}
      <div className="glass-card rounded-lg p-4 space-y-4">
        <Label className="text-base font-semibold">Ajouter une image</Label>
        
        {/* Upload button */}
        <div className="flex gap-3">
          <Button
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            disabled={uploading}
            className="flex-1"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            Importer une image
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>

        {/* Or URL input */}
        <div className="flex items-center gap-2">
          <div className="h-px bg-border flex-1" />
          <span className="text-xs text-muted-foreground">ou coller une URL</span>
          <div className="h-px bg-border flex-1" />
        </div>

        <div className="flex gap-2">
          <Input
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1"
          />
          <Button onClick={handleAddUrl} disabled={!newUrl.trim()}>
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Current images grid */}
      <div>
        <Label className="text-base font-semibold mb-3 block">
          Images actuelles ({images.length})
        </Label>
        
        {images.length === 0 ? (
          <div className="text-center py-8 glass-card rounded-lg">
            <Image className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              Aucune image personnalisée. Les images par défaut sont utilisées.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {images.map((img) => (
              <div
                key={img.id}
                className="relative group aspect-square rounded-lg overflow-hidden bg-muted"
              >
                <img
                  src={img.url}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/150?text=Error";
                  }}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    variant="destructive"
                    size="icon"
                    className="w-8 h-8"
                    onClick={() => handleDeleteImage(img.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-primary/10 rounded-lg p-4 text-sm">
        <p className="font-semibold text-primary mb-2">💡 Conseils</p>
        <ul className="list-disc list-inside text-muted-foreground space-y-1">
          <li>Les images sont affichées avec un filtre sépia pour s'intégrer au thème</li>
          <li>Utilisez des images carrées ou proches du carré pour un meilleur rendu</li>
          <li>Ajoutez au moins 12-16 images pour un mur complet</li>
          <li>Les changements sont visibles immédiatement sur la page d'accueil</li>
        </ul>
      </div>
    </div>
  );
};
